import React from 'react';
import {
    Badge,
    Button,
    Progress,
} from 'reactstrap';
import {
    faTasks, faClockRotateLeft, faExclamationTriangle, faExclamationCircle,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    FlagIconCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    toasty,
    onFieldChange,
    IconButton,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { BillingFlags } from '../billing/Billing';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import InvoiceSlideout from '../invoice/InvoiceSlideout';
import { Modal } from '../common/Modal';
import BillingChargeEmployeeSlideout from './BillingChargeEmployeeSlideout';
import BillingChargeCategorySlideout from './BillingChargeCategorySlideout';

class BillingChargeCategoryIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.billingChargeCategorySlideoutRef = React.createRef();
        this.billingChargeEmployeeSlideoutRef = React.createRef();
        this.timesheetDetailsRef = React.createRef();
        this.invoiceSlideoutRef = React.createRef();

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            billingChargeCategorySlideoutOpen: false,
            selectedDetails: [],
            loadingReport: false,
            yesNo,
            isApproving: false,
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onApprove = async () => {
        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.billingId);
            const response = await httpClient
                .post(ApiRoutes.billingChargeCategory.approve(), model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.error(`Error approving billing records: ${response}`);
            } else {
                toasty.success('Billing records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            rowsSelected: [],
        });
    };

    onBillingChargeCategorySlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    onBillingChargeCategorySlideoutOpen = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.billingChargeCategorySlideoutRef.current.open(id);
        }
    };

    onBillingChargeEmployeeOpenInvoice = async (id) => {
        if (id) {
            const model = {
                billingIds: null,
                invoiceId: id,
            };

            await this.context.setFormOpened(true);
            this.invoiceSlideoutRef.current.open(model);
        }
    };

    onBillingChargeEmployeeOpenTimesheet = async (timesheetId) => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        if (timesheetId) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(timesheetId);
        }
    };

    onBillingChargeEmployeeSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
    };

    onBillingChargeEmployeeSlideoutOpen = async (id) => {
        this.setState({ selectedChargeId: id });

        if (id) {
            await this.context.setFormOpened(true);
            this.billingChargeEmployeeSlideoutRef.current.open(id);
        }
    };

    onChange = onFieldChange;

    onInvoiceSlideoutClosed = async () => {
        const { selectedChargeId } = this.state;

        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        this.onBillingChargeEmployeeSlideoutOpen(selectedChargeId);
    };

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
        }
    };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onTimesheetDetailsClosed = async () => {
        const { selectedChargeId } = this.state;

        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        this.onBillingChargeEmployeeSlideoutOpen(selectedChargeId);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    populateState = async () => {
        const { tenantSettings } = this.context.tenant;

        const [dispatchLocations, chargeTypeUnits, flags] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.chargeTypeUnits()),
            httpClient.js(ApiRoutes.typeAheads.billingCategoryFlags()),
        ]);

        const gridOptions = createGridOptions(this);

        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.rowClassRules = {
            'ag-row-warning': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.BillNotEqualToCharge);

                return matches.length > 0;
            },
            'ag-row-success': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => (
                    f === BillingFlags.ChargeTypeThresholdExceeded
                    || f === BillingFlags.BreakThresholdExceeded
                ));

                return matches.length > 0;
            },
            'ag-row-otjobstart': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.OTJobStartRange);

                return matches.length > 0;
            },
            'ag-row-regoutoforder': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.RegOutOfOrder);

                return matches.length > 0;
            },
            'ag-row-otoutoforder': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.OTOutOfOrder);

                return matches.length > 0;
            },
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            flagIconRenderer: FlagIconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };
        gridOptions.onRowClicked = this.onRowClicked;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const chargeTypeUnitsFilterParams = {
            suppressFilterButton: true,
            options: chargeTypeUnits,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const processingTypeUnitsFilterParams = {
            suppressFilterButton: true,
            options: [
                { label: 'Regular hours out of order', value: 1 },
                { label: 'OT hours out of order', value: 2 },
            ],
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const flagsFilterParams = {
            suppressFilterButton: true,
            options: flags,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Billing.JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'Billing.Timesheet.TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.timesheetId) {
                            return (
                                <Button
                                    color="link"
                                    className="site-link"
                                    disabled={!!this.context.formIsOpen}
                                    title="Open Timesheet"
                                    onClick={() => {
                                        this.openTimesheetDetails(
                                            params.data.timesheetId,
                                        );
                                    }}
                                >
                                    {params.data.timesheetNumber}
                                </Button>
                            );
                        }

                        return params.data.timesheetNumber;
                    }

                    return null;
                },
            },
            {
                colId: 'Billing.CustomerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Billing.DispatchLocationId',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'TimesheetDetail.ResourceName',
                headerName: 'Resource',
                field: 'resourceName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'outOfOrderType',
                minWidth: 85,
                headerName: '',
                sortable: false,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: processingTypeUnitsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: processingTypeUnitsFilterParams,
                cellRenderer: (params) => {
                    if (params.data
                        && params.data.resourceTypeId == 1
                        && ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.RegOutOfOrder).length == 0
                        && ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.OTOutOfOrder).length == 0) {
                        return (
                            <IconButton
                                title="View all charges this period"
                                onClick={() => {
                                    this.onBillingChargeEmployeeSlideoutOpen(params.data.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faClockRotateLeft} />
                            </IconButton>
                        );
                    }

                    if (params.data
                        && params.data.resourceTypeId == 1
                        && ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.RegOutOfOrder).length > 0) {
                        return (
                            <IconButton
                                title="Reg charge is out of order.  View..."
                                onClick={() => {
                                    this.onBillingChargeEmployeeSlideoutOpen(params.data.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </IconButton>
                        );
                    }

                    if (params.data
                        && params.data.resourceTypeId == 1
                        && ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.OTOutOfOrder).length > 0) {
                        return (
                            <IconButton
                                title="OT charge is out of order.  View..."
                                onClick={() => {
                                    this.onBillingChargeEmployeeSlideoutOpen(params.data.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faExclamationCircle} />
                            </IconButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'TimesheetDetail.ChargeTypeName',
                headerName: 'Charge Type',
                field: 'chargeTypeName',
                sortable: true,
                flex: 3,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.hasBreak) {
                            return (
                                <span>
                                    {params.data.chargeTypeName}
                                    <Badge
                                        color="info"
                                        className="ml-2"
                                        style={{
                                            fontSize: '1em',
                                            marginTop: '-2px',
                                        }}
                                        title={`Hours: ${params.data.breakHours}`}
                                    >
                                        B
                                    </Badge>
                                </span>
                            );
                        }

                        return params.data.chargeTypeName;
                    }

                    return null;
                },
            },
            {
                colId: 'TimesheetDetail.ChargeTypeUnitsId',
                headerName: 'Units',
                field: 'chargeTypeUnitsName',
                minWidth: 105,
                sortable: true,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: chargeTypeUnitsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: chargeTypeUnitsFilterParams,
            },
            {
                colId: 'Bill',
                headerName: 'Bill Amt',
                field: 'bill',
                minWidth: 95,
                sortable: false,
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                minWidth: 65,
                sortable: false,
            },
            {
                colId: 'End',
                headerName: 'End',
                field: 'end',
                minWidth: 65,
                sortable: false,
            },
            {
                colId: 'Reg',
                headerName: 'Reg',
                field: 'reg',
                minWidth: 65,
                sortable: false,
            },
        ];

        if (tenantSettings.enableShiftDifferentialBilling) {
            defs.push(
                {
                    colId: 'RSD',
                    headerName: 'RSD',
                    field: 'rsd',
                    minWidth: 65,
                    sortable: false,
                },
            );
        }

        defs.push({
            colId: 'OT',
            headerName: 'OT',
            field: 'ot',
            minWidth: 65,
            sortable: false,
        });

        if (tenantSettings.enableDoubleTimeBilling) {
            defs.push(
                {
                    colId: 'DT',
                    headerName: 'DT',
                    field: 'dt',
                    minWidth: 65,
                    sortable: false,
                },
            );
        }

        defs.push({
            colId: 'IsEmergency',
            headerName: 'Emergency?',
            field: 'isEmergency',
            sortable: false,
            flex: 1.5,
            cellRenderer: (r) => {
                if ((r.data ?? {}).isEmergency) {
                    return 'Yes';
                }

                return 'No';
            },
        });

        defs.push(...[
            {
                colId: 'Id',
                minWidth: 65,
                headerName: 'Edit',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.onBillingChargeCategorySlideoutOpen,
                    idField: 'id',
                    iconClass: 'fa-edit billing-edit-btn',
                },
            },
            {
                colId: 'Flag',
                headerName: 'Flag',
                field: 'flag',
                flex: 1,
                minWidth: 65,
                sortable: false,
                tooltipField: 'flag',
                tooltipComponentParams: { color: '#ececec' },
                cellRenderer: 'flagIconRenderer',
                cellRendererParams: {
                    bodyField: 'flag',
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: flagsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: flagsFilterParams,
            },
            {
                colId: 'SelectionPlaceholder',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                minWidth: 50,
                checkboxSelection: true,
            },
        ]);

        gridOptions.columnDefs = defs;

        const dataSource = createDataSource(
            ApiRoutes.billingChargeCategory.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isRejectingTimesheet: false,
        });
    };

    onApproveButtonClick = () => {
        const approveModalCount = this.state.rowsSelected.length;

        Modal.show({
            title: 'Approve',
            onSubmit: () => this.onApprove(),
            SaveButtonProps: {
                label: 'Approve',
                savingLabel: 'Approving',
                color: 'success',
                icon: faCheck,
            },
            body: () => `Are you sure you would like to approve ${approveModalCount} records?`,
        });
    };

    render() {
        const { tenant } = this.context;
        const { tenantSettings } = tenant;

        const {
            rowData,
            gridOptions,
            isApproving,
            rowsSelected,
        } = this.state;

        if (this.state.loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        // TODO: Eliminate inline styles.
        if (
            !tenantSettings.billingEnabled || !tenantSettings.invoicingEnabled
        ) return <NotAuthorizedPage />;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faTasks}
                        className="mr-2 text-muted"
                    />
                    <span>Billing: Charge Categories</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        <Button
                            size="sm"
                            color="success"
                            style={{ marginRight: '10px' }}
                            onClick={() => this.onApproveButtonClick()}
                            disabled={
                                rowsSelected.length === 0
                                || isApproving
                            }
                        >
                            Approve Selected
                        </Button>
                    </span>
                </PageHeading>

                <DataGridToolbar
                    entity="Billing"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: ApiRoutes.billingChargeCategory.excelExport(),
                        filePrefix: 'BillingChargeCategorySearch',
                    }}
                    serverExportDisabled={
                        !!this.state.saving
                        || !!this.state.loading
                        || !!this.state.loadingData
                    }
                />
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}
                <BillingChargeCategorySlideout
                    ref={this.billingChargeCategorySlideoutRef}
                    show={
                        this.state.billingChargeCategorySlideoutOpen
                    }
                    toggleShow={(open) => this.setState({
                        billingChargeCategorySlideoutOpen: open,
                    })}
                    onClose={
                        this.onBillingChargeCategorySlideoutClosed
                    }
                />
                <BillingChargeEmployeeSlideout
                    ref={this.billingChargeEmployeeSlideoutRef}
                    show={
                        this.state.billingChargeEmployeeSlideoutOpen
                    }
                    toggleShow={(open) => this.setState({
                        billingChargeEmployeeSlideoutOpen: open,
                    })}
                    onClose={
                        this.onBillingChargeEmployeeSlideoutClosed
                    }
                    onOpenInvoice={(invoiceId) => {
                        this.onBillingChargeEmployeeOpenInvoice(invoiceId);
                    }}
                    onOpenTimesheet={(timesheetId) => {
                        this.onBillingChargeEmployeeOpenTimesheet(timesheetId);
                    }}
                />
                <InvoiceSlideout
                    ref={this.invoiceSlideoutRef}
                    show={this.state.invoiceSlideoutOpen}
                    toggleShow={(open) => this.setState({ invoiceSlideoutOpen: open })}
                    onClose={this.onInvoiceSlideoutClosed}
                />
                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({
                        timesheetDetailsOpen: open,
                    })}
                    onClose={this.onTimesheetDetailsClosed}
                    onlyBillable
                    isBilling
                />
            </PageWrap>
        );
    }
}

export default BillingChargeCategoryIndex;
